const definitions = [
  { code: "uncategorized", label: "Uncategorized", direction: "both" },
  { code: "client_payments", label: "Client payments", direction: "incoming" },
  { code: "other_income", label: "Other income", direction: "incoming" },
  { code: "supplier_payments", label: "Supplier payments", direction: "outgoing" },
  { code: "bank_fees", label: "Bank fees & charges", direction: "outgoing" },
  { code: "taxes", label: "Taxes", direction: "outgoing" },
  { code: "payroll", label: "Payroll", direction: "outgoing" },
  { code: "rent", label: "Rent", direction: "outgoing" },
  { code: "utilities", label: "Utilities", direction: "outgoing" },
  { code: "card_purchases", label: "Card purchases", direction: "outgoing" },
  { code: "cash", label: "Cash", direction: "both" },
  { code: "transfers", label: "Transfers", direction: "both" },
  { code: "refunds", label: "Refunds", direction: "both" },
  { code: "financing", label: "Loans & financing", direction: "both" },
  { code: "other_expenses", label: "Other expenses", direction: "outgoing" },
];

export const BANK_TRANSACTION_CATEGORIES = Object.freeze(definitions.map((category) => Object.freeze(category)));
export const BANK_TRANSACTION_CATEGORY_CODES = Object.freeze(BANK_TRANSACTION_CATEGORIES.map((category) => category.code));

const categoryByCode = new Map(BANK_TRANSACTION_CATEGORIES.map((category) => [category.code, category]));
const normalizedText = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const rules = [
  { code: "refunds", key: "refund-keywords", direction: "both", confidence: 0.96, pattern: /\b(refund|reversal|chargeback|rimbursim|kthim)\b/ },
  { code: "bank_fees", key: "bank-fee-keywords", direction: "outgoing", confidence: 0.98, pattern: /\b(bank fee|bank charge|account fee|commission|komision|provizion|tarif\w*|mirembajt\w*|mbajtj\w*)\b/ },
  { code: "taxes", key: "tax-keywords", direction: "outgoing", confidence: 0.95, pattern: /\b(atk|tax|taxes|vat|tvsh|tatim|dogan|customs)\b/ },
  { code: "payroll", key: "payroll-keywords", direction: "outgoing", confidence: 0.94, pattern: /\b(payroll|salary|salaries|wage|wages|paga|page|rroga|rroge)\b/ },
  { code: "rent", key: "rent-keywords", direction: "outgoing", confidence: 0.91, pattern: /\b(rent|rental|lease|qira)\b/ },
  { code: "utilities", key: "utility-keywords", direction: "outgoing", confidence: 0.88, pattern: /\b(keds|kek|electricity|energy|utility|utilities|internet|telecom|water|uje|waste|mbeturina)\b/ },
  { code: "cash", key: "cash-keywords", direction: "both", confidence: 0.94, pattern: /\b(atm|cash withdrawal|cash deposit|terheqje|depozite)\b/ },
  { code: "card_purchases", key: "card-keywords", direction: "outgoing", confidence: 0.9, pattern: /\b(pos|card purchase|card payment|visa|mastercard)\b|\bpurchase\b.*\b\d{6}\s+\d{4}\b/ },
  { code: "financing", key: "financing-keywords", direction: "both", confidence: 0.88, pattern: /\b(loan|loan repayment|credit installment|kredi|huamarrje)\b/ },
  { code: "transfers", key: "internal-transfer-keywords", direction: "both", confidence: 0.9, pattern: /\b(internal transfer|own account|between accounts|transfer to own)\b/ },
  { code: "client_payments", key: "client-payment-keywords", direction: "incoming", confidence: 0.82, pattern: /\b(invoice|fature|customer|client)\b/ },
  { code: "supplier_payments", key: "supplier-payment-keywords", direction: "outgoing", confidence: 0.82, pattern: /\b(invoice|fature|supplier|vendor|furnitor)\b/ },
];

const directionOf = (transaction) => {
  const signed = Number(transaction?.signedAmount);
  if (Number.isFinite(signed) && signed !== 0) return signed > 0 ? "incoming" : "outgoing";
  const credit = Number(transaction?.credit || 0);
  const debit = Number(transaction?.debit || 0);
  return credit > debit ? "incoming" : debit > credit ? "outgoing" : "both";
};

export const suggestBankTransactionCategory = (transaction = {}) => {
  const direction = directionOf(transaction);
  const searchable = normalizedText([
    transaction.counterparty,
    transaction.counterparty_name,
    transaction.description,
    transaction.reference,
  ].filter(Boolean).join(" "));
  const matched = rules.find((rule) => (rule.direction === "both" || rule.direction === direction) && rule.pattern.test(searchable));
  if (matched) return {
    categoryCode: matched.code,
    categorySource: "rule",
    categoryConfidence: matched.confidence,
    categoryRuleKey: matched.key,
  };
  if (direction === "incoming") return { categoryCode: "other_income", categorySource: "direction", categoryConfidence: 0.35, categoryRuleKey: "incoming-fallback" };
  if (direction === "outgoing") return { categoryCode: "other_expenses", categorySource: "direction", categoryConfidence: 0.35, categoryRuleKey: "outgoing-fallback" };
  return { categoryCode: "uncategorized", categorySource: "system", categoryConfidence: null, categoryRuleKey: null };
};

export const bankTransactionCategory = (transaction = {}) => {
  const suggested = suggestBankTransactionCategory(transaction);
  const explicitCode = String(transaction.categoryCode || transaction.category_code || "").trim();
  const code = categoryByCode.has(explicitCode) ? explicitCode : suggested.categoryCode;
  const definition = categoryByCode.get(code) || categoryByCode.get("uncategorized");
  const source = String(transaction.categorySource || transaction.category_source || "").trim() || suggested.categorySource;
  const confidenceValue = transaction.categoryConfidence ?? transaction.category_confidence ?? suggested.categoryConfidence;
  return {
    ...definition,
    source,
    confidence: confidenceValue === null || confidenceValue === undefined ? null : Number(confidenceValue),
    ruleKey: transaction.categoryRuleKey || transaction.category_rule_key || suggested.categoryRuleKey,
    isManual: source === "manual",
  };
};
